'use client';

import { EcEnv, enableGuarantee } from '@castlery/config';
import { Box, Grid, Stack, Typography, useBreakpoints } from '@castlery/fortress';
import { StandardInfo } from '@castlery/fortress/Icons';
import { Product, ProductProperty, Variant, WarrantyOffer } from '@castlery/modules-product-domain';
import { combineProperties } from '@castlery/modules-product-services';
import { CustomLink } from '@castlery/shared-components';
import { useCallback, useMemo, useState } from 'react';
import { ProductAIProperty } from './product-ai-property';
import { PropertyExplanationDrawer } from './property-explanation-drawer';
import { EVENT_PDP_DETAILS } from '@castlery/modules-tracking-services';
import { useAppDispatch } from '@castlery/shared-redux-store';

interface ProductPropertyParisProps {
  propertyName: keyof Product['product_properties'];
  warrantyList?: WarrantyOffer[];
  product?: Product;
  variant?: Variant;
}

export const ProductPropertyParis = (props: ProductPropertyParisProps) => {
  const { product, variant, propertyName, warrantyList, ...rest } = props;
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [explanation, setExplanation] = useState('');
  const { mobile, desktop } = useBreakpoints();

  const properties = useMemo(() => {
    return combineProperties(product?.product_properties[propertyName], variant?.variant_properties[propertyName]);
  }, [product, variant, propertyName]);

  const isSandGrey = useMemo(
    () =>
      variant?.variant_option_values &&
      variant?.variant_option_values?.find((optionValue) => optionValue?.name === 'sand_grey'),
    [variant]
  );

  const trackPdpDetails = useCallback(
    async (pname: string, value: string) => {
      await dispatch(
        EVENT_PDP_DETAILS({
          action: 'product_property',
          label: `${pname} - ${value}`,
        })
      );
    },
    [dispatch]
  );

  const renderValue = useCallback(
    (property: ProductProperty) => {
      switch (property?.name) {
        case 'returns': {
          return (
            <>
              <Typography level="body2">
                {property.value}
                <CustomLink
                  // isExternalFlag={true}
                  href={`/${EcEnv.NEXT_PUBLIC_COUNTRY?.toLowerCase()}/sales-and-refunds`}
                  {...{ role: 'button', target: '_blank' }}
                  onClick={() => {
                    trackPdpDetails(property?.name, property?.value);
                  }}
                  {...{
                    style: {
                      verticalAlign: 'top',
                      display: 'inline-flex',
                    },
                  }}
                  aria-label="View sales and refunds policy"
                >
                  <StandardInfo
                    sx={{
                      width: '20px',
                      height: '20px',
                    }}
                    aria-hidden="true"
                  />
                </CustomLink>
              </Typography>
            </>
          );
        }
        case 'warranty': {
          return (
            <Stack direction="column" gap={2} justifyContent="flex-start">
              <Box>
                <Typography level="body2">
                  {property.value}
                  <CustomLink
                    // isExternalFlag={true}
                    href={`/${EcEnv.NEXT_PUBLIC_COUNTRY?.toLowerCase()}/${enableGuarantee ? 'guarantee' : 'warranty'}`}
                    {...{ role: 'button', target: '_blank' }}
                    onClick={() => {
                      trackPdpDetails(property?.name, property?.value);
                    }}
                    {...{
                      style: {
                        verticalAlign: 'top',
                        display: 'inline-flex',
                      },
                    }}
                    aria-label={`View ${enableGuarantee ? 'guarantee' : 'warranty'} policy`}
                  >
                    <StandardInfo
                      sx={{
                        width: '20px',
                        height: '20px',
                      }}
                      aria-hidden="true"
                    />
                  </CustomLink>
                </Typography>
              </Box>
              {warrantyList && warrantyList?.length > 0 && (
                <Box>
                  <Typography level="body2">
                    {'Mulberry Extended Warranty (Add-On)'}
                    <StandardInfo
                      role="button"
                      onClick={() => {
                        trackPdpDetails('Warranty', 'Mulberry');
                        if (window?.mulberry?.inline?.instances?.[0]?.postMessageClient?.listeners) {
                          window.mulberry.inline.instances[0].postMessageClient.listeners
                            .find((x) => x.key === 'mulberry:inline-to-faq')
                            ?.fn(window.mulberry.core.settings);
                        }
                      }}
                      sx={{
                        verticalAlign: 'top',
                        display: 'inline-block',
                        ml: 2,
                        cursor: 'pointer',
                        width: '20px',
                        height: '20px',
                      }}
                    />
                  </Typography>
                </Box>
              )}
            </Stack>
          );
        }
        case 'assembly_condition': {
          return <ProductAIProperty property={property} />;
        }
        default: {
          return (
            <>
              <Typography level="body2">
                {property?.name === 'fabric_composition' && isSandGrey ? '90% Polyester, 10% Linen' : property.value}
                {property.explanation && (
                  <StandardInfo
                    role="button"
                    onClick={() => {
                      trackPdpDetails(property?.name, property?.value);
                      if (property.explanation) {
                        setOpen(true);
                        setExplanation(property.explanation);
                      }
                    }}
                    sx={{
                      verticalAlign: 'top',
                      display: 'inline-block',
                      ml: 2,
                      cursor: 'pointer',
                      width: '20px',
                      height: '20px',
                    }}
                  />
                )}
              </Typography>
            </>
          );
        }
      }
    },
    [isSandGrey, warrantyList]
  );

  return (
    <>
      <Grid container columnSpacing={6} rowSpacing={desktop ? 5 : 4} sx={{ mt: mobile ? 2 : 3 }} {...rest}>
        {properties?.map((property, index) => (
          <>
            <Grid
              lg={3}
              md={3}
              sm={3}
              xs={5}
              key={`${property.name}-${index}`}
              alignItems="flex-start"
              sx={{
                display: 'flex',
              }}
            >
              <Typography level="body2">{property.presentation}:</Typography>
            </Grid>
            <Grid
              lg={9}
              md={9}
              sm={9}
              xs={7}
              key={`${property.value}-${index}`}
              alignItems="flex-start"
              sx={{
                display: 'flex',
                a: {
                  ml: 2,
                  color: 'var(--fortress-palette-neutral-plainColor)',
                  '&:hover': {
                    color: 'var(--fortress-palette-neutral-plainColor)',
                  },
                },
              }}
            >
              {/* <Typography
                level="body2"
                variant="plain"
                sx={{
                  px: 0,
                  py: 0,
                  display: 'inline',
                }}
              >
                {renderValue(property)}
              </Typography>
              {property.explanation && (
                <IconButton
                  type="button"
                  sx={{
                    width: '24px',
                    height: '24px',
                    verticalAlign: 'text-bottom',
                    ml: 2,
                  }}
                  onClick={() => {
                    数据追踪
                      dispatch({
                        type: EVENT_PDP_DETAILS,
                        result: {
                          detailAction: 'product_property',
                          label: `${property.presentation} - ${property.value}`,
                        },
                      });
                    if (property.explanation) {
                      setOpen(true);
                      setExplanation(property.explanation);
                    }
                  }}
                >
                  <ProductInfoTooltip />
                  <StandardInfo />
                </IconButton>
              )} */}
              {renderValue(property)}
            </Grid>
          </>
        ))}
      </Grid>
      <PropertyExplanationDrawer open={open} onClose={() => setOpen(false)} explanation={explanation} />
    </>
  );
};
